import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Pill, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Sparkles, Loader2, Search, Filter, Tv, Video, Monitor, Radio, Database, CheckCircle2 } from "lucide-react";

export default function OtherTargeting() {
  const [segments, setSegments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [aiQuery, setAiQuery] = useState("");
  const [aiRunning, setAiRunning] = useState(false);
  const [aiResult, setAiResult] = useState(null);

  const load = async () => {
    const [s, c] = await Promise.all([
      api.get(`/other-targeting/segments${category ? `?category=${encodeURIComponent(category)}` : ""}`),
      api.get("/other-targeting/categories"),
    ]);
    setSegments(s.data);
    setCategories(c.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category]);

  const filtered = segments.filter((p) => !q || (p.segment_name + p.description + p.provider).toLowerCase().includes(q.toLowerCase()));

  const runAI = async () => {
    if (!aiQuery.trim()) return;
    setAiRunning(true); setAiResult(null);
    try {
      const { data } = await api.post("/other-targeting/search", { query: aiQuery });
      setAiResult(data);
    } catch (e) {
      setAiResult({ query: aiQuery, matches: [], ai_summary: e?.response?.data?.detail || "Search failed" });
    } finally { setAiRunning(false); }
  };

  const activate = async (p) => {
    const chs = p.channels || [];
    await api.post("/audiences", {
      name: p.segment_name,
      type: "third_party",
      description: `${p.provider} · ${p.category}`,
      estimated_reach: p.estimated_reach,
      cpm_data_fee: p.data_fee_cpm,
      provider: p.provider,
      category: p.category,
      consent_confirmed: true,
      polygon_ids: [],
      tags: ["other-targeting", ...chs.map((c) => c.toLowerCase())],
    });
    toast.success(`Activated: ${p.segment_name}`);
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="other-targeting-page">
      <PageHeader
        eyebrow="Voter Audiences"
        title="Target Other Categories"
        subtitle="Lifestyle, contextual, in-market, professional and demographic segments approved for political use — expand beyond the voter file."
      />

      <Card>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-cyan-300" />
          <div className="text-xs uppercase tracking-wider text-cyan-300 font-semibold">AI Audience Search</div>
        </div>
        <div className="flex flex-col md:flex-row gap-2">
          <input
            value={aiQuery}
            onChange={(e) => setAiQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runAI()}
            placeholder="Describe your goal — e.g. rural small-business owners who watch streaming TV…"
            data-testid="other-ai-input"
            className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-4 py-2.5 text-sm text-white focus:border-cyan-400/60 focus:outline-none"
          />
          <Btn onClick={runAI} disabled={aiRunning || !aiQuery.trim()} data-testid="other-ai-run">
            {aiRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {aiRunning ? "Searching…" : "Search"}
          </Btn>
        </div>

        {aiResult && (
          <div className="mt-4" data-testid="other-ai-results">
            <div className="text-xs text-slate-400 mb-3">{aiResult.ai_summary}</div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {aiResult.matches.map((m) => (
                <div key={m.provider_id} className="rounded-md border border-cyan-400/30 bg-cyan-400/5 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-semibold">{m.provider} · {m.category}</div>
                      <div className="font-display text-base font-bold mt-0.5">{m.segment_name}</div>
                      <div className="text-xs text-slate-400 mt-1">{m.match_reason}</div>
                    </div>
                    <Pill status="Active">Ready</Pill>
                  </div>
                  <div className="mt-2 flex items-center gap-3 text-xs">
                    <span className="text-slate-400">Reach <span className="text-white font-mono">{m.estimated_reach.toLocaleString()}</span></span>
                    <span className="text-slate-400">CPM <span className="text-white font-mono">${m.client_data_fee_cpm ?? m.data_fee_cpm}</span></span>
                    <span className="flex gap-1">
                      {m.channels.map((c) => <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-cyan-300">{c}</span>)}
                    </span>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Btn variant="secondary" onClick={() => activate(m)} data-testid={`other-ai-activate-${m.provider_id}`}>Activate</Btn>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setCategory("")}
          data-testid="other-cat-all"
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${!category ? "bg-cyan-400/20 border-cyan-400/40 text-cyan-300" : "bg-white/5 border-white/10 text-slate-300 hover:border-white/20"}`}
        >All</button>
        {categories.map((c) => (
          <button
            key={c.category}
            onClick={() => setCategory(c.category)}
            data-testid={`other-cat-${c.category.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${category === c.category ? "bg-cyan-400/20 border-cyan-400/40 text-cyan-300" : "bg-white/5 border-white/10 text-slate-300 hover:border-white/20"}`}
          >
            {c.category} <span className="text-slate-500">· {c.count}</span>
          </button>
        ))}
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter segments…" className="w-full bg-slate-900/80 border border-white/15 rounded-md pl-9 pr-3 py-2 text-sm" data-testid="other-filter" />
          </div>
          <div className="ml-auto text-xs text-slate-400 flex items-center gap-1"><Filter className="w-3 h-3" /> {filtered.length} of {segments.length}</div>
        </div>

        {filtered.length === 0 ? <EmptyState icon={Database} title="No segments match" description="Try a different category or filter." /> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filtered.map((p) => (
              <div key={p.id} className="rounded-md border border-white/10 bg-white/[0.02] p-4 hover:border-cyan-400/30 transition-colors" data-testid={`other-segment-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-semibold">{p.provider} · {p.category}</div>
                    <div className="font-display text-lg font-bold mt-0.5">{p.segment_name}</div>
                    <div className="text-xs text-slate-400 mt-1">{p.description}</div>
                  </div>
                  <Pill status="Active">Ready</Pill>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-slate-400">Reach <span className="text-white font-mono">{p.estimated_reach.toLocaleString()}</span></span>
                  <span className="text-slate-400">CPM <span className="text-white font-mono">${p.client_data_fee_cpm ?? p.data_fee_cpm}</span></span>
                  <span className="text-slate-400">Geo <span className="text-white font-mono">{p.geography}</span></span>
                  <span className="ml-auto flex gap-1">
                    {p.ctv_eligible && <ChannelBadge icon={Tv} label="CTV" />}
                    {p.video_eligible && <ChannelBadge icon={Video} label="Video" />}
                    {p.display_eligible && <ChannelBadge icon={Monitor} label="Display" />}
                    {p.audio_eligible && <ChannelBadge icon={Radio} label="Audio" />}
                  </span>
                </div>
                <div className="mt-3 flex justify-end">
                  <Btn variant="secondary" onClick={() => activate({ ...p, channels: [p.ctv_eligible && "CTV", p.video_eligible && "Video", p.display_eligible && "Display", p.audio_eligible && "Audio"].filter(Boolean) })} data-testid={`other-activate-${p.id}`}>
                    <CheckCircle2 className="w-4 h-4" /> Activate audience
                  </Btn>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

const ChannelBadge = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-cyan-400/10 text-cyan-300 border border-cyan-400/20">
    <Icon className="w-2.5 h-2.5" /> {label}
  </span>
);
